import React, { FC, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { RoomsSection } from "../../../../models/manageWebSite";
import EditModal from "./EditModal";

interface Section8Props {
  data: RoomsSection | null;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const Section8: FC<Section8Props> = ({ data, flag, setFlag, homeId }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [galleryLightboxVisible, setGalleryLightboxVisible] = useState(false);

  const roomEntries = data?.roomEntries || [];
  const selectedRoom = roomEntries.find((room) => room.roomId === selectedRoomId) || null;
  const galleryImages = selectedRoom
    ? (selectedRoom.signedImageUrls || [])
    : roomEntries.flatMap((room) => room.signedImageUrls || []);
  const activeGalleryImage = galleryImages[activeGalleryIndex];

  const openGalleryLightbox = (index: number) => {
    setActiveGalleryIndex(index);
    setGalleryLightboxVisible(true);
  };

  const showPreviousGalleryImage = () => {
    if (galleryImages.length === 0) {
      return;
    }
    setActiveGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const showNextGalleryImage = () => {
    if (galleryImages.length === 0) {
      return;
    }
    setActiveGalleryIndex((prev) => (prev + 1) % galleryImages.length);
  };

  return (
    <div style={{ width: "100%", background: "linear-gradient(180deg, #eff6ff 0%, #f8fafc 100%)", padding: "3rem 1.25rem" }}>
      <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
        <div className="flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div>
            <div style={{ color: "#185da2", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", fontSize: "0.8rem" }}>
              Optional Page
            </div>
            <div style={{ color: "#0f172a", fontSize: "2rem", fontWeight: 800, marginTop: "0.65rem" }}>
              Rooms
            </div>
            <div style={{ color: "#64748b", marginTop: "0.65rem", lineHeight: 1.7, maxWidth: "760px" }}>
              Add a dedicated rooms page with private or shared options, bathroom details, amenities, pricing notes, and room photos.
            </div>
          </div>
          <Button label="Edit Rooms Page" severity="warning" className="website-section-action-button" onClick={() => setOpenModal(true)} />
        </div>

        <div className="grid">
          <div className="col-12 lg:col-8">
            <div style={{ background: "#ffffff", border: "1px solid #dbe7f3", borderRadius: "28px", padding: "1.5rem", height: "100%", boxShadow: "0 18px 38px rgba(15,61,99,0.08)" }}>
              <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
                <div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#0f172a" }}>{data?.title || "Rooms and home features"}</div>
                  <div style={{ color: "#64748b", lineHeight: 1.7, marginTop: "0.75rem" }}>{data?.description}</div>
                </div>
                <span style={{ display: "inline-flex", alignItems: "center", borderRadius: "999px", padding: "0.45rem 0.85rem", background: data?.enabled ? "#ecfdf3" : "#fef2f2", color: data?.enabled ? "#047857" : "#b91c1c", fontWeight: 700 }}>
                  {data?.enabled ? "Page enabled" : "Page hidden"}
                </span>
              </div>

              <div className="grid mt-4" style={{ rowGap: "0.75rem" }}>
                {roomEntries.map((room) => (
                  <div className="col-12 md:col-6" key={room.roomId}>
                    <div
                      onClick={() => setSelectedRoomId((current) => (current === room.roomId ? null : room.roomId))}
                      style={{
                        background: selectedRoomId === room.roomId ? "#f8fbff" : "#ffffff",
                        border: selectedRoomId === room.roomId ? "1px solid #93c5fd" : "1px solid #dbe7f3",
                        borderRadius: "24px",
                        padding: "1.4rem",
                        height: "100%",
                        boxShadow: selectedRoomId === room.roomId ? "0 18px 34px rgba(24,93,162,0.12)" : "0 16px 32px rgba(15,61,99,0.06)",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <div className="flex align-items-start justify-content-between gap-2 flex-wrap">
                        <div>
                          <div style={{ color: "#185da2", fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: "0.1em" }}>{room.spaceType || "Room"}</div>
                          <div style={{ color: "#0f172a", fontWeight: 800, fontSize: "1.2rem", marginTop: "0.55rem", lineHeight: 1.3 }}>{room.name}</div>
                        </div>
                        <span style={{ background: "#ffffff", border: "1px solid #dbe7f3", borderRadius: "999px", padding: "0.3rem 0.65rem", color: "#475569", fontWeight: 700, fontSize: "0.8rem" }}>
                          {(room.signedImageUrls || []).length} photos
                        </span>
                      </div>
                      {(room.bedroomOption || room.bathroomOption) ? (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {room.bedroomOption ? <span style={{ background: "#eff6ff", color: "#185da2", borderRadius: "999px", padding: "0.45rem 0.8rem", fontWeight: 700, fontSize: "0.82rem" }}>{room.bedroomOption}</span> : null}
                          {room.bathroomOption ? <span style={{ background: "#fff7ed", color: "#b45309", borderRadius: "999px", padding: "0.45rem 0.8rem", fontWeight: 700, fontSize: "0.82rem" }}>{room.bathroomOption}</span> : null}
                        </div>
                      ) : null}
                      {room.description ? (
                        <div style={{ color: "#64748b", lineHeight: 1.9, marginTop: "1rem", fontSize: "0.98rem" }}>{room.description}</div>
                      ) : null}
                      {(room.amenities || []).length ? (
                        <ul style={{ paddingLeft: "1.1rem", margin: "1.1rem 0 0", lineHeight: 1.95, color: "#475569" }}>
                          {(room.amenities || []).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {data?.pricingNotes ? (
                <div style={{ marginTop: "1.5rem", background: "#ffffff", border: "1px dashed #bfdbfe", borderRadius: "22px", padding: "1.2rem 1.25rem", color: "#475569", lineHeight: 1.8 }}>
                  <strong style={{ color: "#0f172a" }}>Pricing note:</strong> {data.pricingNotes}
                </div>
              ) : null}
            </div>
          </div>
          <div className="col-12 lg:col-4">
            <div style={{ background: "#ffffff", border: "1px solid #dbe7f3", borderRadius: "28px", padding: "1.25rem", boxShadow: "0 18px 38px rgba(15,61,99,0.08)", height: "100%" }}>
              <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                <div>
                  <div style={{ color: "#0f172a", fontWeight: 800, fontSize: "1.1rem" }}>Room gallery</div>
                  <div style={{ color: "#64748b", marginTop: "0.5rem", lineHeight: 1.7 }}>
                    {selectedRoom ? `Showing photos for ${selectedRoom.name || selectedRoom.spaceType || "selected room"}.` : `${roomEntries.length} room or shared space entr${roomEntries.length === 1 ? "y" : "ies"} configured for the sub-page.`}
                  </div>
                </div>
                {selectedRoom ? (
                  <Button type="button" label="All Rooms" outlined size="small" onClick={() => setSelectedRoomId(null)} />
                ) : null}
              </div>
              <div className="grid mt-4" style={{ rowGap: "0.5rem" }}>
                {galleryImages.map((image, index) => (
                  <div className="col-6" key={`${image}-${index}`}>
                    <button
                      type="button"
                      onClick={() => openGalleryLightbox(index)}
                      style={{
                        width: "100%",
                        minHeight: "150px",
                        borderRadius: "20px",
                        background: `url(${image}) center/cover no-repeat`,
                        border: "1px solid #dbe7f3",
                        boxShadow: "0 14px 26px rgba(15,61,99,0.08)",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <EditModal isOpen={openModal} toggleModal={setOpenModal} flag={flag} setFlag={setFlag} data={data} homeId={homeId} />
      <Dialog
        visible={galleryLightboxVisible}
        onHide={() => setGalleryLightboxVisible(false)}
        dismissableMask
        maximized
        focusOnShow={false}
        style={{ background: "#020617" }}
        contentStyle={{ background: "#020617", padding: 0 }}
        headerStyle={{ background: "#020617", color: "#ffffff", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        header={galleryImages.length > 0 ? `Room Gallery ${activeGalleryIndex + 1} of ${galleryImages.length}` : "Room Gallery"}
      >
        {activeGalleryImage ? (
          <div
            className="flex align-items-center justify-content-center"
            style={{ minHeight: "calc(100vh - 8rem)", gap: "1rem", padding: "1rem", background: "#020617" }}
          >
            <Button icon="pi pi-chevron-left" rounded outlined severity="contrast" autoFocus onClick={showPreviousGalleryImage} />
            <div className="flex align-items-center justify-content-center" style={{ flex: 1, height: "100%" }}>
              <img
                src={activeGalleryImage}
                alt={`Room gallery ${activeGalleryIndex + 1}`}
                style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: "18px" }}
              />
            </div>
            <Button icon="pi pi-chevron-right" rounded outlined severity="contrast" onClick={showNextGalleryImage} />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

export default Section8;
